"use client";

/**
 * Client body for the governed-HR-answers use-case page. Lives apart from
 * `page.tsx` (which stays a server component for `metadata`) so it can call
 * `useT()` and follow the visitor's locale, exactly like the other use-case
 * pages. All copy comes from the `ucHrGovernance` namespace; structure (icons,
 * hrefs) stays here.
 *
 * Every claim rendered here traces to docs/hr-governance.md — see the header of
 * src/i18n/locales/marketing/uc-hr-governance.ts. The demo video is embedded
 * through <VideoBlock>, which renders an honest "being finished" note until the
 * final deliverable is published in the video manifest — never an old cut.
 */

import {
  MessageCircleQuestion,
  UserCheck,
  ShieldAlert,
  EyeOff,
  UserPlus,
  ShieldCheck,
  Eye,
  Layers,
  Database,
  PlugZap,
  History,
  Lock,
  Building2,
} from "lucide-react";
import { MarketingShell } from "@/components/marketing/marketing-shell";
import {
  UseCaseHero,
  Section,
  SectionHeader,
  WorkflowSteps,
  FeatureGrid,
  Faq,
  SecurityNote,
  RelatedLinks,
  CtaBanner,
} from "@/components/marketing/marketing-kit";
import { VideoBlock } from "@/components/marketing/video/video-block";
import { useT } from "@/i18n";

export function HrGovernedAnswersContent() {
  const t = useT();
  const k = (s: string) => `ucHrGovernance.${s}`;

  const steps = [
    { key: "ask", icon: <MessageCircleQuestion size={18} /> },
    { key: "identity", icon: <UserCheck size={18} /> },
    { key: "classify", icon: <ShieldAlert size={18} /> },
    { key: "minimize", icon: <EyeOff size={18} /> },
    { key: "route", icon: <UserPlus size={18} /> },
    { key: "approve", icon: <ShieldCheck size={18} /> },
  ].map((s) => ({
    icon: s.icon,
    tag: t(k(`steps.${s.key}.tag`)),
    title: t(k(`steps.${s.key}.title`)),
    body: t(k(`steps.${s.key}.body`)),
  }));

  const features = [
    { key: "visibility", icon: <Eye size={18} /> },
    { key: "sensitivity", icon: <Layers size={18} /> },
    { key: "minimization", icon: <Database size={18} /> },
    { key: "connectors", icon: <PlugZap size={18} /> },
    { key: "correction", icon: <History size={18} /> },
    { key: "audit", icon: <ShieldCheck size={18} /> },
  ].map((f) => ({
    icon: f.icon,
    title: t(k(`features.${f.key}.title`)),
    body: t(k(`features.${f.key}.body`)),
  }));

  const faq = ["q1", "q2", "q3", "q4", "q5", "q6", "q7", "q8"].map((q) => ({
    q: t(k(`faq.${q}.q`)),
    a: t(k(`faq.${q}.a`)),
  }));

  return (
    <MarketingShell>
      <UseCaseHero
        eyebrow={t(k("hero.eyebrow"))}
        title={t(k("hero.title"))}
        highlight={t(k("hero.highlight"))}
        subtitle={t(k("hero.subtitle"))}
        primary={{ href: "/demo", label: t(k("hero.primaryCta")) }}
        secondary={{ href: "/contact", label: t(k("hero.secondaryCta")) }}
        bullets={[t(k("hero.bullet1")), t(k("hero.bullet2")), t(k("hero.bullet3"))]}
      />

      <Section id="video">
        <SectionHeader
          eyebrow={t(k("video.eyebrow"))}
          title={t(k("video.title"))}
          description={t(k("video.description"))}
        />
        <div className="mt-10">
          <VideoBlock id="fixcontrol-hr-nl" posterCaption={t(k("video.title"))} />
        </div>
      </Section>

      <Section muted>
        <SectionHeader eyebrow={t(k("faq.eyebrow"))} title={t(k("faq.title"))} />
        <Faq items={faq} />
      </Section>

      <Section>
        <SectionHeader
          eyebrow={t(k("how.eyebrow"))}
          title={t(k("how.title"))}
          description={t(k("how.description"))}
        />
        <WorkflowSteps steps={steps} />
      </Section>

      <Section muted>
        <SectionHeader
          eyebrow={t(k("features.eyebrow"))}
          title={t(k("features.title"))}
          description={t(k("features.description"))}
        />
        <FeatureGrid features={features} />
      </Section>

      <Section>
        <SectionHeader eyebrow={t(k("buildBuy.eyebrow"))} title={t(k("buildBuy.title"))} />
        <SecurityNote icon={<Building2 size={18} />} title={t(k("buildBuy.noteTitle"))}>
          {t(k("buildBuy.noteBody"))}
        </SecurityNote>
      </Section>

      <Section muted>
        <SectionHeader eyebrow={t(k("governed.eyebrow"))} title={t(k("governed.title"))} />
        <SecurityNote icon={<Lock size={18} />} title={t(k("governed.noteTitle"))}>
          {t(k("governed.noteBody"))}
        </SecurityNote>
      </Section>

      <Section>
        <RelatedLinks
          title={t("mk.relatedTitle")}
          links={[
            {
              href: "/use-cases/ai-agent-approval-ledger",
              label: t(k("related.approvalLedger.label")),
              description: t(k("related.approvalLedger.description")),
            },
            {
              href: "/integrations/teams",
              label: t(k("related.teams.label")),
              description: t(k("related.teams.description")),
            },
            {
              href: "/integrations/slack",
              label: t(k("related.slack.label")),
              description: t(k("related.slack.description")),
            },
          ]}
        />
      </Section>

      <CtaBanner
        title={t(k("cta.title"))}
        description={t(k("cta.description"))}
        primary={{ href: "/demo", label: t(k("cta.primary")) }}
        secondary={{ href: "/contact", label: t(k("cta.secondary")) }}
      />
    </MarketingShell>
  );
}
